import { useMemo, useCallback, useContext } from 'react';
import { type StateSelector, type State } from '@aidkitorg/types/lib/survey';
import { assertUnreachable } from '@aidkitorg/types/lib/typechecks';
import { STATES, CODE_BY_STATE } from '@aidkitorg/types/lib/address/states';
import { Select, SelectOptionType } from "@aidkitorg/component-library";
import { useModularMarkdown } from '../Hooks/ModularMarkdown';
import { type DistroQuestionProps } from './Props';
import { generateAccessibleLabel } from '../utils/generateAccessibleLabel';
import InterfaceContext from '../Context';

const ALL_STATE_NAMES = STATES.map(({ name }) => name);

export function StateSelector({
  component,
  info,
  setInfo,
  questionProps,
}: DistroQuestionProps<StateSelector>) {
  const context = useContext(InterfaceContext);
  const header = useModularMarkdown({ content: component.header ?? '', info });

  const statesToUse = useMemo(() => {
    switch (component.limitTo?.kind) {
      case undefined:
        return ALL_STATE_NAMES;
      case 'Only': {
        const limitToSet = new Set(component.limitTo.states);
        return ALL_STATE_NAMES.filter((name) => limitToSet.has(name));
      }
      case 'Excluding': {
        const limitToSet = new Set(component.limitTo.states);
        return ALL_STATE_NAMES.filter((name) => !limitToSet.has(name));
      }
      default:
        assertUnreachable(component.limitTo);
    }
  }, [component.limitTo]);

  const options = useMemo(() => {
    return statesToUse.map((state) => ({ label: state, value: state }))
  }, [statesToUse]);

  const selectedOption = useMemo(() => {
    return options.find(({ value }) => value === info[component.targetField]);
  }, [options, info[component.targetField]]);

  const handleSelectOption = useCallback((state: SelectOptionType<State> | undefined) => {
    setInfo(component.targetField, state?.value ?? '');
    setInfo(component.targetField + '_code', state ? CODE_BY_STATE[state.value] : '');
  }, [component.targetField]);

  const accessibleLabel = component.header
    ? generateAccessibleLabel(component.header[context.lang])
    : '';

  if ((questionProps['Additional Options'] || []).indexOf('Hidden') !== -1) {
    return <span></span>;
  }

  return (
    <Select
      options={options}
      value={selectedOption}
      onChange={handleSelectOption}
      disabled={questionProps.blockQuestionEdits}
      description={accessibleLabel}
      label={header}
      searchable
      portal
      clearable
    />
  );
};
